import { LocalDateTime } from "@js-joda/core";
import { API } from "../API";
import * as clockInOutUtils from "../clockInOutUtils";
import { isLoading, Loading } from "../Loading";
import {
  CaregiverId,
  ClockInOutCallType,
  VisitInstanceClockinClockoutRequestParams,
  VisitInstanceId,
} from "../messages";
import { VisitInstanceDetails } from "../messages/Visit";
import { AppState } from "../system/AppState";
import { useClockInOutModals } from "./ClockInOutModals";

interface Props {
  visitInstance: VisitInstanceDetails | Loading;
  caregiverId: CaregiverId | Loading;
  isCaregiverOfMedflyt: boolean;
  api: API;
  appState: AppState;
  onRequireManualClock: (visitInstanceId: VisitInstanceId) => void;
  onUploadVerificationSelfie: (
    visitInstanceId: VisitInstanceId,
    imageBase64: string,
    clockType: ClockInOutCallType
  ) => Promise<void>;
  onModalClose?: (visitDetails: VisitInstanceDetails | Loading) => void;
  onRequestMissedVisit?: (visitInstanceId: VisitInstanceId) => Promise<void>;
}

const useVisitInstanceClockInOut = (props: Props) => {
  const {
    state: clockInOutModalsState,
    actions: { promptClock },
  } = useClockInOutModals({
    isCaregiverOfMedflyt: props.isCaregiverOfMedflyt,
    onRequireManualClock: props.onRequireManualClock,
    caregiverId: props.caregiverId,
    onCheckIfDocumentsSubmitted: props.api.checkIfAllDocumentsSubmitted,
    onClickClock: clock,
    onClockPresentVisit: () => null,
    onModalClose: () => props.onModalClose?.(props.visitInstance),
    onUploadVerificationSelfie: props.onUploadVerificationSelfie,
    onRequestVisitAdjustmentCheck: props.api.getCaregiverCanAdjustVisitInstanceTimes,
    onRequestMissedVisit: props.onRequestMissedVisit || props.api.caregiverMarkVisitAsMissed,
  });

  async function clock(
    visitInstanceId: VisitInstanceId,
    params: VisitInstanceClockinClockoutRequestParams,
    clockType: ClockInOutCallType
  ) {
    const success = await props.api.setCaregiverVisitInstanceClockInClockOut(
      visitInstanceId,
      clockType,
      params
    );

    if (success) {
      props.appState.setVisitInstanceOrTaskVisitInstanceClockInOutTimeByType(
        visitInstanceId,
        clockType,
        LocalDateTime.now()
      );
    }

    return success;
  }

  const promptClockInMapModal = () => {
    if (isLoading(props.appState.clockedInStatus) || isLoading(props.visitInstance)) {
      throw new Error("Can't prompt clock in modal while data is loading");
    }

    promptClock({
      clockType: ClockInOutCallType.IN,
      clockedInStatus: props.appState.clockedInStatus,
      visitInstance: props.visitInstance,
    });
  };

  const promptClockOutMapModal = () => {
    if (isLoading(props.appState.clockedInStatus) || isLoading(props.visitInstance)) {
      throw new Error("Can't clock out while visit instance is loading");
    }

    promptClock({
      clockType: ClockInOutCallType.OUT,
      clockedInStatus: props.appState.clockedInStatus,
      visitInstance: props.visitInstance,
    });
  };

  const canClockIn = () => {
    if (isLoading(props.appState.clockedInStatus) || isLoading(props.visitInstance)) {
      return false;
    }

    return clockInOutUtils.getClockInStrategy(props.visitInstance).valid;
  };

  const canClockOut = () => {
    if (isLoading(props.visitInstance)) {
      return false;
    }

    return clockInOutUtils.getClockOutStrategy(props.visitInstance).valid;
  };

  const canSubmitTimesheet = () => {
    if (isLoading(props.appState.clockedInStatus) || isLoading(props.visitInstance)) {
      return false;
    }

    const clockInStrategy = clockInOutUtils.getClockInStrategy(props.visitInstance);
    const clockOutStrategy = clockInOutUtils.getClockOutStrategy(props.visitInstance);

    return (
      (clockInStrategy.valid && clockInStrategy.strategy === "Timesheet") ||
      (clockOutStrategy.valid && clockOutStrategy.strategy === "Timesheet")
    );
  };

  return {
    canClockIn: canClockIn,
    canClockOut: canClockOut,
    canSubmitTimesheet: canSubmitTimesheet,
    promptClockInMapModal: promptClockInMapModal,
    promptClockOutMapModal: promptClockOutMapModal,
    clockInOutModalsState: clockInOutModalsState,
  };
};

export default useVisitInstanceClockInOut;
